// src/components/ContactFlowViewer.js - Enhanced with collapsible pane and simplified routing

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Modal, Card, Group, Text, Badge, Stack,
  Tabs, ScrollArea, ActionIcon, Tooltip, Select, Alert, Menu,
  Collapse, SegmentedControl, Switch, Box
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconX, IconDownload, IconChartBar, IconFileAnalytics,
  IconAlertTriangle, IconInfoCircle, IconTopologyStar, IconPhoto,
  IconFileTypePng, IconFileTypeJpg, IconFileTypeSvg,
  IconRoute, IconChevronLeft, IconChevronRight, IconSettings,
  IconKey, IconCloud, IconCloudUpload, IconSparkles, IconMaximize
} from '@tabler/icons-react';
import { ContactFlowDiagram } from '../diagrams/ContactFlowDiagram';
import { RoutingPresets, RoutingConfiguration } from '../diagrams/routing';
import { ContactFlowLucidParser } from '../diagrams/contactFlowLucidParser';
import { resetLucidMeasurementCache } from '../diagrams/utils/lucidTextMeasurement';
import { toPng, toJpeg, toSvg } from 'html-to-image';
import JSZip from 'jszip';
import ReactMarkdown from 'react-markdown';
import LucidAuthModal from './LucidAuthModal';
import { lucidAuthService } from '../../../shared/services/lucidAuthService';
import { lucidExportService } from '../../../shared/services/lucidExportService';

// Wait two animation frames so DOM style/layout changes (element resize + viewport
// transform) are applied before html-to-image rasterizes. Replaces a fixed 500ms
// delay that added up across batch exports.
const waitForFrames = () => new Promise(resolve =>
  requestAnimationFrame(() => requestAnimationFrame(resolve))
);

// Fallback cap on how long batch export waits for a flow's diagram to signal readiness,
// in case the readiness callback is ever missed (e.g. switching to an already-selected flow).
const RENDER_SAFETY_TIMEOUT_MS = 2500;

// Resolve once the diagram's node elements are actually in the DOM (bounded), so we never
// rasterize an empty viewport. Returns true if nodes were found, false if it gave up.
const waitForNodesPainted = async (reactFlowElement, maxFrames = 30) => {
  for (let i = 0; i < maxFrames; i++) {
    if (reactFlowElement.querySelectorAll('.react-flow__node').length > 0) return true;
    await new Promise(resolve => requestAnimationFrame(resolve));
  }
  return false;
};

const ContactFlowViewer = ({
  opened,
  onClose,
  flows = [],
  summary = null,
  instanceAlias = 'Unknown Instance',
  exportedData = null,
  instanceId = null,
  selectedInstanceIds = [],
  connectInstances = [],
  routingConfig = RoutingPresets.BALANCED
}) => {
  const [selectedFlowIndex, setSelectedFlowIndex] = useState(0);
  const [selectedNodeData, setSelectedNodeData] = useState(null);
  const [selectedEdgeData, setSelectedEdgeData] = useState(null);
  const [layoutDirection, setLayoutDirection] = useState('TB');
  const [isExporting, setIsExporting] = useState(false);
  const [rightPaneCollapsed, setRightPaneCollapsed] = useState(false);
  const [isBatchExporting, setIsBatchExporting] = useState(false);
  const [batchExportProgress, setBatchExportProgress] = useState({ current: 0, total: 0, flowName: '', batch: 0, totalBatches: 0 });
  const [viewType, setViewType] = useState('flows');

  // Advanced routing state (always enabled, but configurable)
  const [currentRoutingConfig, setCurrentRoutingConfig] = useState(routingConfig);
  const [showRoutingStats, setShowRoutingStats] = useState(false);

  // Instance selection state for multi-instance support
  const [currentInstanceId, setCurrentInstanceId] = useState(instanceId);

  const [batchExportConfig, setBatchExportConfig] = useState({
    batchSize: 12, // Slightly reduce batch size for better quality processing
    reduceQuality: false, // Default to high quality - users can opt-in to faster mode
    maxBatches: 20 // Maximum number of batches to prevent too many files
  });

  // Bulk export filters for name-based filtering
  const [bulkExportFilters, setBulkExportFilters] = useState({
    skipDefaultFlows: true,
    skipSampleFlows: true
  });

  // Download local copy option for single Lucid exports
  const [downloadLocalCopy, setDownloadLocalCopy] = useState(false);

  // Lucid Charts integration state
  const [lucidAuthModalOpen, setLucidAuthModalOpen] = useState(false);
  const [isLucidAuthenticated, setIsLucidAuthenticated] = useState(false);
  const [isLucidExporting, setIsLucidExporting] = useState(false);
  const [lucidExportProgress, setLucidExportProgress] = useState({ current: 0, total: 0, flowName: '', status: 'idle' });

  // AI Flow Summary modal state
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);

  // State to control AI summary visibility in diagram (for exports only)
  const [showSummaryInDiagram, setShowSummaryInDiagram] = useState(false);

  // Update currentInstanceId when instanceId prop changes
  useEffect(() => {
  if (instanceId && instanceId !== currentInstanceId) {
      setCurrentInstanceId(instanceId);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  // Check Lucid authentication status on component mount
  useEffect(() => {
    if (opened) {
      const authenticated = lucidAuthService.isAuthenticated();
      setIsLucidAuthenticated(authenticated);
    }
  }, [opened]);

  // Ref to access the React Flow instance from the diagram
  const diagramRef = useRef(null);

  // One-shot resolver set by the batch export loop and invoked when the currently
  // mounted diagram reports it has finished rendering (see handleDiagramRenderComplete).
  const renderResolverRef = useRef(null);
  const handleDiagramRenderComplete = useCallback(() => {
    renderResolverRef.current?.();
  }, []);

  //const currentFlows = selectedInstanceIds.length > 1 
  //  ? flows.filter(flow => flow.instanceId === currentInstanceId)
  //  : flows;

  const currentFlows = useMemo(() => {
    let filteredFlows;
    if (selectedInstanceIds.length > 1) {
      filteredFlows = flows.filter(flow =>
        flow.instanceId === currentInstanceId &&
        flow.resourceType === viewType.slice(0, -1) // 'flows' -> 'flow', 'modules' -> 'module'
      );
    } else {
      filteredFlows = flows.filter(flow =>
        flow.resourceType === viewType.slice(0, -1)
      );
    }

    // Sort flows alphabetically by name
    return filteredFlows.sort((a, b) =>
      a.flowName.localeCompare(b.flowName, undefined, { sensitivity: 'base' })
    );
  }, [flows, selectedInstanceIds, currentInstanceId, viewType]);

  const selectedFlow = currentFlows[selectedFlowIndex];

  // Get current instance info
  const currentInstance = connectInstances.find(inst => inst.Id === currentInstanceId);
  const currentInstanceAlias = currentInstance?.InstanceAlias || instanceAlias;

  // The user's selected routing config (currentRoutingConfig) is passed straight to the
  // diagram. ContactFlowDiagram owns the single size-based defaulting so the heuristic
  // isn't applied twice; an explicit preset selection always wins over the size default.

  // Handle node click in diagram
  const handleNodeClick = useCallback((node) => {
    setSelectedNodeData(node);
    setSelectedEdgeData(null);
  }, []);

  // Handle edge click in diagram
  const handleEdgeClick = useCallback((edge) => {
    setSelectedEdgeData(edge);
    setSelectedNodeData(null);
  }, []);

    // Handle flow selection
    const handleFlowChange = (value) => {
    const index = parseInt(value);
    setSelectedFlowIndex(index);
    setSelectedNodeData(null);
    setSelectedEdgeData(null);
    };

    // Handle instance selection
    const handleInstanceChange = (newInstanceId) => {
    setCurrentInstanceId(newInstanceId);
    setSelectedFlowIndex(0); // Reset to first flow of new instance
    setSelectedNodeData(null);
    setSelectedEdgeData(null);
    };

  // Handle routing configuration change
  const handleRoutingConfigChange = useCallback((newConfig) => {
    setCurrentRoutingConfig(newConfig);
    
    // Force diagram re-render with new config
    setTimeout(() => {
      if (diagramRef.current?.forceRerender) {
        diagramRef.current.forceRerender();
      }
    }, 100);
  }, []);

  // Utility functions
  const downloadImage = useCallback((dataUrl, format) => {
    const link = document.createElement('a');
    const fileName = `${selectedFlow.flowName.replace(/[^a-zA-Z0-9\s-_]/g, '')}-diagram.${format}`;
    link.download = fileName;
    link.href = dataUrl;
    link.click();
  },[selectedFlow]);

  // PNG Export using html2canvas
  const exportPNG = useCallback(async (reactFlowElement, viewport, originalTransform, totalWidth, totalHeight, minX, minY, padding, panelWidth = 0) => {
    
    // toPng imported at module top
    
    reactFlowElement.style.width = `${totalWidth}px`;
    reactFlowElement.style.height = `${totalHeight}px`;
    reactFlowElement.style.position = 'relative';
    reactFlowElement.style.overflow = 'visible';

    if (viewport) {
      const offsetX = minX - padding - panelWidth; // Shift right to make room for panels
      const offsetY = minY - padding;
      viewport.style.transform = `translate(${-offsetX}px, ${-offsetY}px) scale(1)`;
    }

    await waitForFrames();

    const exportOptions = {
      pixelRatio: 2,
      backgroundColor: '#ffffff',
      width: totalWidth,
      height: totalHeight,
      cacheBust: true,
      useCORS: true,
      allowTaint: true,
      filter: (node) => {
        if (node.classList) {
          return !node.classList.contains('react-flow__controls') &&
                 !node.classList.contains('react-flow__minimap') &&
                 !node.classList.contains('react-flow__attribution')
        }
        return true;
      }
    };

    const dataUrl = await toPng(reactFlowElement, exportOptions);
    downloadImage(dataUrl, 'png');
  },[downloadImage]);

  // JPEG Export
  const exportJPEG = useCallback(async (reactFlowElement, viewport, originalTransform, totalWidth, totalHeight, minX, minY, padding, panelWidth = 0) => {
    
    // toJpeg imported at module top
    
    reactFlowElement.style.width = `${totalWidth}px`;
    reactFlowElement.style.height = `${totalHeight}px`;
    reactFlowElement.style.position = 'relative';
    reactFlowElement.style.overflow = 'visible';

    if (viewport) {
      const offsetX = minX - padding - panelWidth; // Shift right to make room for panels
      const offsetY = minY - padding;
      viewport.style.transform = `translate(${-offsetX}px, ${-offsetY}px) scale(1)`;
    }

    await waitForFrames();

    const exportOptions = {
      quality: 0.95,
      pixelRatio: 2,
      backgroundColor: '#ffffff',
      width: totalWidth,
      height: totalHeight,
      cacheBust: true,
      useCORS: true,
      allowTaint: true,
      filter: (node) => {
        if (node.classList) {
          return !node.classList.contains('react-flow__controls') &&
                 !node.classList.contains('react-flow__minimap') &&
                 !node.classList.contains('react-flow__attribution')
        }
        return true;
      }
    };

    const dataUrl = await toJpeg(reactFlowElement, exportOptions);
    downloadImage(dataUrl, 'jpeg');
  },[downloadImage]);

  // SVG Export using html-to-image
const exportSVG = useCallback(async (reactFlowElement, viewport, originalTransform, totalWidth, totalHeight, minX, minY, padding, panelWidth = 0) => {
    
    // toSvg imported at module top
    
    reactFlowElement.style.width = `${totalWidth}px`;
    reactFlowElement.style.height = `${totalHeight}px`;
    reactFlowElement.style.position = 'relative';
    reactFlowElement.style.overflow = 'visible';

    if (viewport) {
      const offsetX = minX - padding - panelWidth; // Shift right to make room for panels
      const offsetY = minY - padding;
      viewport.style.transform = `translate(${-offsetX}px, ${-offsetY}px) scale(1)`;
    }

    await waitForFrames();

    const exportOptions = {
      backgroundColor: '#ffffff',
      width: totalWidth,
      height: totalHeight,
      cacheBust: true,
      filter: (node) => {
        if (node.classList) {
          return !node.classList.contains('react-flow__controls') &&
                 !node.classList.contains('react-flow__minimap') &&
                 !node.classList.contains('react-flow__attribution')
        }
        return true;
      }
    };

    const dataUrl = await toSvg(reactFlowElement, exportOptions);
    downloadImage(dataUrl, 'svg');
  },[downloadImage]);

  // Fallback export method
  const exportFallbackMethod = useCallback(async (format) => {
    
    const reactFlowElement = document.querySelector('.react-flow');
    if (!reactFlowElement) {
      throw new Error('React Flow element not found');
    }

    // toPng/toJpeg/toSvg imported at module top
    const exportFunction = format === 'jpeg' ? toJpeg : format === 'svg' ? toSvg : toPng;

    const dataUrl = await exportFunction(reactFlowElement, {
    quality: format === 'jpeg' ? 0.95 : 1.0,
    pixelRatio: format === 'svg' ? 1 : 2,
    backgroundColor: '#ffffff',
    cacheBust: true,
    filter: (node) => {
        if (node.classList) {
        return !node.classList.contains('react-flow__controls') &&
                !node.classList.contains('react-flow__minimap') &&
                !node.classList.contains('react-flow__attribution')
        }
        return true;
    }
    });

    downloadImage(dataUrl, format);
  },[downloadImage]);

  // Export diagram function with better error handling
  const exportDiagramAsImage = useCallback(async (format = 'png') => {
    if (!selectedFlow || isExporting || !diagramRef.current) return;

    try {
      setIsExporting(true);

      // Enable AI summary in diagram for export
      setShowSummaryInDiagram(true);

      // Wait for React to render the summary panel
      await new Promise(resolve => setTimeout(resolve, 150));

      const reactFlowElement = document.querySelector('.react-flow');
      if (!reactFlowElement) {
        throw new Error('React Flow element not found');
      }

      const reactFlowInstance = diagramRef.current?.getReactFlowInstance?.();
      
      if (!reactFlowInstance) {
        await exportFallbackMethod(format);
        return;
      }

      const nodes = reactFlowInstance.getNodes();
      
      if (nodes.length === 0) {
        throw new Error('No nodes found in the flow');
      }

      // Bounds calculation for better export
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

      nodes.forEach(node => {
        const nodeWidth = node.width || 200;
        const nodeHeight = node.height || 100;
        
        minX = Math.min(minX, node.position.x);
        minY = Math.min(minY, node.position.y);
        maxX = Math.max(maxX, node.position.x + nodeWidth);
        maxY = Math.max(maxY, node.position.y + nodeHeight);
      });

      const padding = 100;
      const panelWidth = 350; // Extra space for left-side panels (summary + legend)
      const totalWidth = Math.max(800, maxX - minX + padding * 2 + panelWidth);
      const totalHeight = Math.max(600, maxY - minY + padding * 2);

      // Store original styles
      const originalContainerStyle = {
        width: reactFlowElement.style.width,
        height: reactFlowElement.style.height,
        position: reactFlowElement.style.position,
        overflow: reactFlowElement.style.overflow
      };

      const viewport = reactFlowElement.querySelector('.react-flow__viewport');
      const originalTransform = viewport ? viewport.style.transform : '';

      try {
        if (format === 'png') {
          await exportPNG(reactFlowElement, viewport, originalTransform, totalWidth, totalHeight, minX, minY, padding, panelWidth);
        } else if (format === 'jpeg') {
          await exportJPEG(reactFlowElement, viewport, originalTransform, totalWidth, totalHeight, minX, minY, padding, panelWidth);
        } else if (format === 'svg') {
          await exportSVG(reactFlowElement, viewport, originalTransform, totalWidth, totalHeight, minX, minY, padding, panelWidth);
        }

      } finally {
        // Always restore original styles
        Object.assign(reactFlowElement.style, originalContainerStyle);
        if (viewport) {
          viewport.style.transform = originalTransform;
        }
      }

      // Show success notification
      const formatName = format.toUpperCase();
      const messageContent = (
        <div style={{
          maxHeight: '400px',
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          userSelect: 'text'
        }}>
          {`📄 Flow: "${selectedFlow.flowName}"
📷 Format: ${formatName}

File downloaded successfully!`}
        </div>
      );

      notifications.show({
        title: '✓ Diagram Export Complete!',
        message: messageContent,
        color: 'green',
        autoClose: 5000,
        styles: {
          root: { overflow: 'visible' },
          body: { overflow: 'visible' }
        }
      });

    } catch (error) {
      console.error('Failed to export diagram:', error);
      notifications.show({
        title: 'Failed to Export Diagram',
        message: error.message,
        color: 'red',
        autoClose: false,
      });
    } finally {
      setIsExporting(false);
      // Hide AI summary from diagram after export
      setShowSummaryInDiagram(false);
    }
  }, [selectedFlow, isExporting, exportPNG, exportJPEG, exportSVG, exportFallbackMethod]);

  // Capture flow diagram function - moved before exportAllFlowsAsImages to resolve dependency order
  const captureFlowDiagram = useCallback(async (format, flowName, reduceQuality = false) => {
    if (!diagramRef.current) {
      throw new Error('Diagram reference not available');
    }
    
    try {
      // Use the same DOM selection pattern as the working exportDiagramAsImage function
      const reactFlowElement = document.querySelector('.react-flow');
      if (!reactFlowElement) {
        throw new Error('React Flow element not found');
      }

      const reactFlowInstance = diagramRef.current?.getReactFlowInstance?.();
      
      if (!reactFlowInstance) {
        // Fallback to the same approach as single export
        // toPng/toJpeg/toSvg imported at module top
        const exportFunction = format === 'jpg' ? toJpeg : format === 'svg' ? toSvg : toPng;

        const dataUrl = await exportFunction(reactFlowElement, {
          quality: format === 'jpg' ? (reduceQuality ? 0.8 : 0.95) : 1.0,
          pixelRatio: reduceQuality ? 1 : 2,
          backgroundColor: '#ffffff',
          cacheBust: true,
          useCORS: true,
          allowTaint: true,
          filter: (node) => {
            if (node.classList) {
              return !node.classList.contains('react-flow__controls') &&
                     !node.classList.contains('react-flow__minimap') &&
                     !node.classList.contains('react-flow__attribution')
            }
            return true;
          }
        });

        // Convert data URL to blob
        const response = await fetch(dataUrl);
        const imageBlob = await response.blob();
        
        return imageBlob;
      }

      const nodes = reactFlowInstance.getNodes();
      
      if (nodes.length === 0) {
        throw new Error('No nodes found in the flow');
      }

      // Bounds calculation for better export (same as single export)
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

      nodes.forEach(node => {
        const nodeWidth = node.width || 200;
        const nodeHeight = node.height || 100;
        
        minX = Math.min(minX, node.position.x);
        minY = Math.min(minY, node.position.y);
        maxX = Math.max(maxX, node.position.x + nodeWidth);
        maxY = Math.max(maxY, node.position.y + nodeHeight);
      });

      const padding = 100;
      const panelWidth = 350; // Extra space for left-side panels (summary + legend) - same as single export
      const totalWidth = Math.max(800, maxX - minX + padding * 2 + panelWidth);
      const totalHeight = Math.max(600, maxY - minY + padding * 2);

      // Store original styles
      const originalContainerStyle = {
        width: reactFlowElement.style.width,
        height: reactFlowElement.style.height,
        position: reactFlowElement.style.position,
        overflow: reactFlowElement.style.overflow
      };

      // Drive the capture view through React Flow's store (setViewport) rather than writing
      // viewport.style.transform directly — React Flow owns that transform and would otherwise
      // clobber a raw style write on its next render (which caused tiny/blank exports).
      const prevViewport = reactFlowInstance.getViewport ? reactFlowInstance.getViewport() : null;

      let imageBlob;

      try {
        // Apply the same style modifications as single export
        reactFlowElement.style.width = `${totalWidth}px`;
        reactFlowElement.style.height = `${totalHeight}px`;
        reactFlowElement.style.position = 'relative';
        reactFlowElement.style.overflow = 'visible';

        // Equivalent of translate(-offsetX,-offsetY) scale(1): show the whole diagram at
        // natural size, shifted right to leave room for the left-side panels.
        const offsetX = minX - padding - panelWidth;
        const offsetY = minY - padding;
        if (reactFlowInstance.setViewport) {
          reactFlowInstance.setViewport({ x: -offsetX, y: -offsetY, zoom: 1 });
        }

        // Ensure nodes are painted, then let the new viewport transform commit before capture.
        await waitForNodesPainted(reactFlowElement);
        await waitForFrames();

        // Determine capture options based on format and quality settings
        const baseOptions = {
          backgroundColor: '#ffffff',
          pixelRatio: reduceQuality ? 1 : 2,
          width: totalWidth,
          height: totalHeight,
          cacheBust: true,
          useCORS: true,
          allowTaint: true,
          filter: (node) => {
            if (node.classList) {
              return !node.classList.contains('react-flow__controls') &&
                     !node.classList.contains('react-flow__minimap') &&
                     !node.classList.contains('react-flow__attribution')
            }
            return true;
          }
        };

        if (format === 'svg') {
          // SVG export
          // toSvg imported at module top
          const svgString = await toSvg(reactFlowElement, baseOptions);
          
          // Convert SVG string to blob
          imageBlob = new Blob([svgString], { type: 'image/svg+xml' });
          
        } else if (format === 'jpg') {
          // JPG export
          // toJpeg imported at module top
          const jpegString = await toJpeg(reactFlowElement, {
            ...baseOptions,
            quality: reduceQuality ? 0.8 : 0.95
          });
          
          // Convert data URL to blob
          const response = await fetch(jpegString);
          imageBlob = await response.blob();
          
        } else {
          // PNG export (default)
          // toPng imported at module top
          const pngString = await toPng(reactFlowElement, baseOptions);
          
          // Convert data URL to blob
          const response = await fetch(pngString);
          imageBlob = await response.blob();
        }

      } finally {
        // Always restore original styles and viewport
        Object.assign(reactFlowElement.style, originalContainerStyle);
        if (prevViewport && reactFlowInstance.setViewport) {
          reactFlowInstance.setViewport(prevViewport);
        }
      }

      return imageBlob;

    } catch (error) {
      console.error(`Error capturing ${flowName}:`, error);
      throw error;
    }
  }, [diagramRef]);
  const exportAllFlowsAsImages = useCallback(async (format = 'png') => {
    if (currentFlows.length === 0 || isBatchExporting || isExporting) return;

    try {
      setIsBatchExporting(true);

      // Enable AI summary in diagram for bulk export
      setShowSummaryInDiagram(true);

      // Apply bulk export filters based on flow name
      let flowsToExport = [...currentFlows];
      if (bulkExportFilters.skipDefaultFlows) {
        flowsToExport = flowsToExport.filter(flow => !flow.flowName.startsWith('Default '));
      }
      if (bulkExportFilters.skipSampleFlows) {
        flowsToExport = flowsToExport.filter(flow => !flow.flowName.startsWith('Sample '));
      }

      setBatchExportProgress({ current: 0, total: flowsToExport.length, flowName: '', batch: 0, totalBatches: 0 });

      const originalFlowIndex = selectedFlowIndex;
      
      // Calculate optimal batch size based on flow count
      let optimalBatchSize = batchExportConfig.batchSize;
      if (flowsToExport.length > 100) {
        optimalBatchSize = Math.max(5, Math.min(10, batchExportConfig.batchSize)); // Smaller batches for large datasets
      } else if (flowsToExport.length > 50) {
        optimalBatchSize = Math.max(10, Math.min(15, batchExportConfig.batchSize));
      }

      const totalBatches = Math.ceil(flowsToExport.length / optimalBatchSize);
      const timestamp = new Date().toISOString().split('T')[0];
      const baseFileName = `${currentInstanceAlias.replace(/[^a-zA-Z0-9\s-_]/g, '')}-${viewType === 'modules' ? 'Modules' : 'ContactFlows'}-${format.toUpperCase()}`;
      
      let totalSuccessCount = 0;
      let totalErrorCount = 0;
      const allErrors = [];
      
      // Process flows in batches
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const startIdx = batchIndex * optimalBatchSize;
        const endIdx = Math.min(startIdx + optimalBatchSize, flowsToExport.length);
        const batchFlows = flowsToExport.slice(startIdx, endIdx);

        setBatchExportProgress({
          current: startIdx,
          total: flowsToExport.length,
          flowName: `Batch ${batchIndex + 1}/${totalBatches}`,
          batch: batchIndex + 1,
          totalBatches
        });
        
        const zip = new JSZip();
        const batchFolderName = totalBatches > 1 ? `${baseFileName}-Batch${batchIndex + 1}` : baseFileName;
        const folder = zip.folder(batchFolderName);
        
        let batchSuccessCount = 0;
        let batchErrorCount = 0;
        const batchErrors = [];
        
        // Process each flow in the current batch
        for (let i = 0; i < batchFlows.length; i++) {
          const flow = batchFlows[i];
          const globalIndex = startIdx + i;
          
          try {
            setBatchExportProgress({
              current: globalIndex + 1,
              total: flowsToExport.length,
              flowName: flow.flowName,
              batch: batchIndex + 1,
              totalBatches
            });

            // Switch to the current flow
            const flowIndexInCurrentFlows = currentFlows.findIndex(f => f.flowId === flow.flowId);
            if (flowIndexInCurrentFlows !== -1) {
              // Switch flows and wait for the diagram to actually signal it has rendered,
              // rather than a fixed delay. A safety timeout guards against a missed signal
              // (e.g. when the target flow is already the selected one, so no remount occurs).
              await new Promise((resolve) => {
                let settled = false;
                const finish = () => {
                  if (settled) return;
                  settled = true;
                  clearTimeout(timer);
                  renderResolverRef.current = null;
                  resolve();
                };
                const timer = setTimeout(finish, RENDER_SAFETY_TIMEOUT_MS);
                renderResolverRef.current = finish;
                setSelectedFlowIndex(flowIndexInCurrentFlows);
              });

              // Capture the diagram
              const imageBlob = await captureFlowDiagram(format, flow.flowName, batchExportConfig.reduceQuality);
              
              if (imageBlob) {
                const fileName = `${flow.flowName.replace(/[^a-zA-Z0-9\s-_]/g, '')}.${format}`;
                folder.file(fileName, imageBlob);
                batchSuccessCount++;
              } else {
                throw new Error('Failed to capture diagram');
              }
              
            } else {
              throw new Error('Flow not found in current flows');
            }

          } catch (error) {
            console.error(`Failed to export flow ${flow.flowName}:`, error);
            batchErrorCount++;
            batchErrors.push({ flowName: flow.flowName, error: error.message });
            allErrors.push({ flowName: flow.flowName, error: error.message, batch: batchIndex + 1 });
          }
          
          // No fixed inter-flow delay needed: the readiness wait above and the capture's
          // own frame wait already yield to the browser between flows.
        }
        
        // Generate and download the batch ZIP file
        if (batchSuccessCount > 0) {
          try {
            const zipBlob = await zip.generateAsync({ 
              type: 'blob',
              compression: 'DEFLATE',
              compressionOptions: { level: 6 } // Balanced compression
            });
            
            const url = URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = totalBatches > 1 
              ? `${batchFolderName}-${timestamp}.zip`
              : `${baseFileName}-${timestamp}.zip`;
            link.click();
            URL.revokeObjectURL(url);
            
          } catch (zipError) {
            console.error(`Failed to generate ZIP for batch ${batchIndex + 1}:`, zipError);
            batchErrorCount += batchSuccessCount; // Count as errors if ZIP generation fails
          }
        }
        
        totalSuccessCount += batchSuccessCount;
        totalErrorCount += batchErrorCount;
        
        // Force memory cleanup between batches
        if (batchIndex < totalBatches - 1) {
          // Clear the zip object
          zip.remove(batchFolderName);
          
          // Force garbage collection hint (browsers may ignore this)
          if (window.gc) {
            window.gc();
          }
          
          // Longer delay between batches for memory cleanup
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      // Restore original flow selection
      setSelectedFlowIndex(originalFlowIndex);
      
      // Show completion summary

      if (totalErrorCount > 0) {
        const errorSummary = allErrors.slice(0, 10).map(e => `• ${e.flowName}: ${e.error}`).join('\n');
        const moreErrors = allErrors.length > 10 ? `\n... and ${allErrors.length - 10} more errors` : '';

        const errorMessageContent = (
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            userSelect: 'text'
          }}>
            {`📊 Export Summary:
• Successfully Exported: ${totalSuccessCount} flows
• Failed: ${totalErrorCount} flows
• Downloaded: ${totalBatches > 1 ? `${totalBatches} ZIP files` : '1 ZIP file'}

❌ Errors (showing first 10):
${errorSummary}${moreErrors}`}
          </div>
        );

        notifications.show({
          title: '⚠️ Batch Image Export Completed with Errors',
          message: errorMessageContent,
          color: 'yellow',
          autoClose: false,
          styles: {
            root: { overflow: 'visible' },
            body: { overflow: 'visible' }
          }
        });
      } else {
        const successMessageContent = (
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            userSelect: 'text'
          }}>
            {`📊 Export Summary:
• Flows Exported: ${totalSuccessCount}
• Files Downloaded: ${totalBatches > 1 ? `${totalBatches} ZIP files` : '1 ZIP file'}

All flows exported successfully!`}
          </div>
        );

        notifications.show({
          title: '✓ Batch Image Export Complete!',
          message: successMessageContent,
          color: 'green',
          autoClose: 10000,
          styles: {
            root: { overflow: 'visible' },
            body: { overflow: 'visible' }
          }
        });
      }

    } catch (error) {
      console.error('Failed to complete batch export:', error);
      notifications.show({
        title: 'Failed to Complete Batch Export',
        message: error.message,
        color: 'red',
        autoClose: false,
      });
    } finally {
      setIsBatchExporting(false);
      setBatchExportProgress({ current: 0, total: 0, flowName: '', batch: 0, totalBatches: 0 });
      // Hide AI summary from diagram after bulk export
      setShowSummaryInDiagram(false);
    }
  }, [currentFlows, selectedFlowIndex, currentInstanceAlias, isBatchExporting, isExporting, batchExportConfig, captureFlowDiagram, viewType, bulkExportFilters]);

  // Export all flows data with information
  const handleExportData = () => {
    const exportData = flows.map(flow => ({
      name: flow.flowName,
      id: flow.flowId,
      type: flow.flowType,
      nodeCount: flow.nodes?.length || 0,
      edgeCount: flow.edges?.length || 0,
      validation: flow.validation,
      metadata: flow.metadata,
      features: flow.features || {},
      hasDataLookup: !!(exportedData && instanceId),
      routingConfig: currentRoutingConfig
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contact-flows-${instanceAlias}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Lucid Charts Export Functions
  const handleLucidAuth = async () => {
    try {
      setLucidAuthModalOpen(true);
    } catch (error) {
      console.error('Error opening Lucid auth modal:', error);
    }
  };

  const handleLucidAuthSuccess = (result) => {
    setIsLucidAuthenticated(true);
    setLucidAuthModalOpen(false);
  };

  const handleLucidAuthError = (error) => {
    console.error('Lucid authentication error:', error);
    notifications.show({
      title: 'Lucid Authentication Failed',
      message: error.message,
      color: 'red',
      autoClose: false,
    });
  };

  // Utility function to parse Contact Flow Content field
  const parseContactFlowContent = useCallback((originalFlow, flowName) => {
    if (!originalFlow) {
      throw new Error('Original flow data is missing');
    }

    if (!originalFlow.Content) {
      throw new Error('Contact Flow Content field is missing');
    }

    let actualFlowContent = null;
    try {
      if (typeof originalFlow.Content === 'string') {
        actualFlowContent = JSON.parse(originalFlow.Content);
      } else if (typeof originalFlow.Content === 'object') {
        actualFlowContent = originalFlow.Content;
      } else {
        throw new Error('Content field is neither string nor object');
      }

      // Validate the parsed content has the expected structure
      if (!actualFlowContent.Actions || !Array.isArray(actualFlowContent.Actions)) {
        throw new Error('Parsed content missing Actions array');
      }

      if (actualFlowContent.Actions.length === 0) {
        throw new Error('Parsed content has empty Actions array');
      }

      if (!actualFlowContent.StartAction) {
        console.warn(`${flowName}: Parsed content missing StartAction - flow may not render properly`);
      }
      
      return actualFlowContent;
      
    } catch (contentError) {
      console.error(`${flowName}: ❌ Failed to parse Content field:`, contentError);
      throw new Error(`Invalid Contact Flow Content: ${contentError.message}`);
    }
  }, []);

  const exportToLucid = useCallback(async () => {
    if (!selectedFlow || isLucidExporting) return;

    try {
      // Check authentication first
      if (!isLucidAuthenticated) {
        setLucidAuthModalOpen(true);
        return;
      }

      // Parse the Contact Flow Content field to get the actual flow definition
      const actualFlowContent = parseContactFlowContent(selectedFlow.originalFlow, selectedFlow.flowName);

      setIsLucidExporting(true);
      setLucidExportProgress({ 
        current: 1, 
        total: 1, 
        flowName: selectedFlow.flowName,
        status: 'processing'
      });

      // Create ContactFlowLucidParser instance with the actual parsed flow content
      const parser = new ContactFlowLucidParser(
        actualFlowContent,    // ← Now contains the actual flow definition with Actions array
        exportedData,         // All exported AWS resources for intelligent lookup
        currentInstanceId,
        layoutDirection,
        currentRoutingConfig, // Pass routing configuration to constructor
        false,                // Disable verbose logging to reduce console noise
        {                     // Flow metadata for Summary box
          Name: selectedFlow.flowName,
          Type: selectedFlow.flowType,
          Description: selectedFlow.originalFlow.Description || '',
          summary: selectedFlow.originalFlow.summary || null
        }
      );

      const lucidData = parser.convertToLucid();

      if (!lucidData || !lucidData.pages || lucidData.pages.length === 0) {
        throw new Error('Parser returned invalid lucid data');
      }

      if (!lucidData.pages[0].shapes || lucidData.pages[0].shapes.length === 0) {
        throw new Error('Parser returned lucid data with no shapes - this should not happen with valid flow content!');
      }

      // Prepare metadata with folder organization parameters
      const metadata = {
        flowName: selectedFlow.flowName,
        flowId: selectedFlow.flowId,
        flowType: selectedFlow.flowType,
        instanceAlias: currentInstanceAlias,
        instanceId: currentInstanceId,
        instanceName: currentInstanceAlias, // For folder naming
        viewType: null // No subfolder - place directly in main instance folder
      };

      // Export directly to Lucid using the professional diagram data
      const result = await lucidExportService.exportSingleDiagramDirect(lucidData, metadata, downloadLocalCopy);

      setLucidExportProgress({ 
        current: 1, 
        total: 1, 
        flowName: selectedFlow.flowName,
        status: 'completed'
      });

      // Show success message with folder link
      const folderUrl = result.folderInfo?.folderUrl;
      const messageContent = (
        <div>
          <div style={{ marginBottom: '8px' }}>
            Flow: "{selectedFlow.flowName}"
          </div>
          {folderUrl && (
            <a
              href={folderUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#228be6',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              Link To Folder
            </a>
          )}
        </div>
      );

      notifications.show({
        title: '✓ Successfully Exported to Lucid Charts',
        message: messageContent,
        color: 'green',
        autoClose: 10000
      });

    } catch (error) {
      console.error('Lucid export failed:', error);
      console.error('Error stack:', error.stack);
      setLucidExportProgress({ 
        current: 1, 
        total: 1, 
        flowName: selectedFlow.flowName,
        status: 'failed'
      });
      notifications.show({
        title: 'Export to Lucid Failed',
        message: error.message,
        color: 'red',
        autoClose: false,
      });
    } finally {
      setIsLucidExporting(false);
      setTimeout(() => {
        setLucidExportProgress({ current: 0, total: 0, flowName: '', status: 'idle' });
      }, 2000);
    }
  }, [selectedFlow, isLucidAuthenticated, isLucidExporting, currentInstanceAlias, currentInstanceId, exportedData, layoutDirection, currentRoutingConfig, parseContactFlowContent, downloadLocalCopy]);

  const exportAllToLucid = useCallback(async () => {
    if (currentFlows.length === 0 || isLucidExporting) return;

    try {
      // Check authentication first
      if (!isLucidAuthenticated) {
        setLucidAuthModalOpen(true);
        return;
      }

      setIsLucidExporting(true);

      // Clear the Lucid text-measurement cache once per bulk run to bound memory growth.
      resetLucidMeasurementCache();

      // Apply bulk export filters based on flow name
      let flowsToExport = [...currentFlows];
      if (bulkExportFilters.skipDefaultFlows) {
        flowsToExport = flowsToExport.filter(flow => !flow.flowName.startsWith('Default '));
      }
      if (bulkExportFilters.skipSampleFlows) {
        flowsToExport = flowsToExport.filter(flow => !flow.flowName.startsWith('Sample '));
      }

      // Prepare diagrams data using direct transformation from AWS Connect data
      const diagramsData = await Promise.all(flowsToExport.map(async (flow, index) => {
        try {
          // Parse the Contact Flow Content field using the utility function
          const actualFlowContent = parseContactFlowContent(flow.originalFlow, flow.flowName);

          // Create ContactFlowLucidParser instance with the actual parsed flow content
          const parser = new ContactFlowLucidParser(
            actualFlowContent,    // ← Now contains the actual flow definition with Actions array
            exportedData,         // All exported AWS resources for intelligent lookup
            currentInstanceId,
            layoutDirection,
            currentRoutingConfig, // Pass routing configuration to constructor
            false,                // Disable verbose logging to reduce console noise
            {                     // Flow metadata for Summary box
              Name: flow.flowName,
              Type: flow.flowType,
              Description: flow.originalFlow.Description || '',
              summary: flow.originalFlow.summary || null
            }
          );

          const lucidData = parser.convertToLucid();

          return {
            lucidData, // Direct professional Lucid diagram data
            metadata: {
              flowName: flow.flowName,
              flowId: flow.flowId,
              flowType: flow.flowType,
              instanceAlias: currentInstanceAlias,
              instanceId: currentInstanceId,
              instanceName: currentInstanceAlias, // For folder naming
              viewType: null // No subfolder - place directly in main instance folder
            }
          };
        } catch (error) {
          console.error(`Failed to transform flow ${flow.flowName}:`, error);
          // Return fallback data for failed transformations
          return {
            lucidData: null,
            metadata: {
              flowName: flow.flowName,
              flowId: flow.flowId,
              flowType: flow.flowType,
              instanceAlias: currentInstanceAlias,
              instanceId: currentInstanceId,
              instanceName: currentInstanceAlias,
              viewType: null, // No subfolder - place directly in main instance folder
              transformationFailed: true,
              error: error.message
            }
          };
        }
      }));

      // Export all diagrams as multi-page Lucid document(s)
      const results = await lucidExportService.exportMultipleDiagramsAsMultiPageDocument(diagramsData, {
        downloadLocalCopy: downloadLocalCopy, // Pass the download local copy option
        onProgress: (progress) => {
          setLucidExportProgress({
            current: progress.current,
            total: progress.total,
            flowName: progress.flowName,
            status: progress.status
          });
        },
        onError: (error) => {
          console.error('Lucid multi-page export error:', error);
        }
      });

      // Show completion summary
      const failureCount = results.failed.length;
      const totalFlows = results.total;
      const folderUrl = results.folderInfo?.folderUrl;

      // Build simplified message content
      const messageContent = (
        <div>
          <div style={{ marginBottom: '6px' }}>
            Total Flows Exported: {totalFlows}
          </div>
          <div style={{ marginBottom: '10px' }}>
            Documents Created: {results.documentCount || 1}
          </div>
          {folderUrl && (
            <div style={{ marginBottom: '10px' }}>
              <a
                href={folderUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#228be6',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                Link To Folder
              </a>
            </div>
          )}
          {failureCount > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ color: '#fa5252', fontWeight: 'bold', marginBottom: '6px' }}>
                ⚠️ Failures: {failureCount} document(s)
              </div>
              {results.failed.length <= 3 && (
                <div>
                  <div style={{ marginBottom: '4px' }}>Errors:</div>
                  {results.failed.map((failure, idx) => (
                    <div key={idx} style={{ marginLeft: '8px', marginBottom: '2px' }}>
                      • Document {failure.documentIndex + 1}: {failure.error}
                    </div>
                  ))}
                </div>
              )}
              {results.failed.length > 3 && (
                <div style={{ fontStyle: 'italic', fontSize: '0.9em' }}>
                  (See console for detailed error information)
                </div>
              )}
            </div>
          )}
        </div>
      );

      notifications.show({
        title: '✓ Bulk Export to Lucid Charts Complete!',
        message: messageContent,
        color: failureCount > 0 ? 'yellow' : 'green',
        autoClose: failureCount > 0 ? false : 15000
      });

    } catch (error) {
      console.error('Lucid batch export failed:', error);
      notifications.show({
        title: 'Batch Export to Lucid Failed',
        message: error.message,
        color: 'red',
        autoClose: false,
      });
    } finally {
      setIsLucidExporting(false);
      setTimeout(() => {
        setLucidExportProgress({ current: 0, total: 0, flowName: '', status: 'idle' });
      }, 2000);
    }
  }, [currentFlows, isLucidAuthenticated, isLucidExporting, currentInstanceAlias, currentInstanceId, exportedData, layoutDirection, currentRoutingConfig, parseContactFlowContent, bulkExportFilters, downloadLocalCopy]);

    // Flow selector options (filtered by current instance and type)
    const flowOptions = currentFlows.map((flow, index) => ({
      value: index.toString(),
      label: `${flow.flowName} (${flow.nodes?.length || 0} nodes)`
    }));

    // Instance selector options
    const instanceOptions = selectedInstanceIds.length > 1 
    ? selectedInstanceIds.map(instId => {
        const instance = connectInstances.find(inst => inst.Id === instId);
        const instanceFlowCount = flows.filter(flow => flow.instanceId === instId).length;
        return {
            value: instId,
            label: `${instance?.InstanceAlias || instId} (${instanceFlowCount} flows)`
        };
        })
    : [];

  if (!opened) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
    <Group>
        <IconTopologyStar size={20} />
        <Text fw={500}>
        Contact Flow Diagrams - {currentInstanceAlias}
        {selectedInstanceIds.length > 1 && (
            <Badge variant="light" size="sm" ml="xs">{selectedInstanceIds.length} instances</Badge>
        )}
        </Text>
    </Group>
    }
      size="95%"
      styles={{
        modal: { height: '95vh' },
        body: { height: 'calc(95vh - 60px)', display: 'flex', flexDirection: 'column' }
      }}
    >
      <Stack gap="md" style={{ height: '100%' }}>
        {/* Enhanced Summary Section */}
        {summary && (
          <Alert icon={<IconInfoCircle size={16} />} color="blue">
            <Group>
              <Text size="sm">
                <strong>{currentFlows.length}</strong> {viewType} in current instance,
                <strong> {summary.generated}</strong> total processed, 
                <strong> {summary.skipped}</strong> skipped, 
                <strong> {summary.errors}</strong> errors
                {selectedInstanceIds.length > 1 && (
                  <> • <strong>{flows.length}</strong> items across all instances</>
                )}
              </Text>
              {summary.validationResults && summary.validationResults.length > 0 && (
                <Text size="sm">
                  • <strong>{summary.validationResults.filter(r => r.isValid).length}</strong> valid {viewType}
                </Text>
              )}
            </Group>
          </Alert>
        )}

        {/* Enhanced Flow Selection and Controls */}
        <Group justify="space-between">
            <Group>
              {/* Flow/Module Type Selector */}
              <SegmentedControl
                value={viewType}
                onChange={(value) => {
                  setViewType(value);
                  setSelectedFlowIndex(0); // Reset selection when switching types
                  setSelectedNodeData(null);
                  setSelectedEdgeData(null);
                }}
                data={[
                  { label: 'Flows', value: 'flows' },
                  { label: 'Modules', value: 'modules' }
                ]}
                size="sm"
              />
              
              {/* Instance Selector - only show if multiple instances */}
              {selectedInstanceIds.length > 1 && (
                <Select
                  placeholder="Select instance"
                  data={instanceOptions}
                  value={currentInstanceId}
                  onChange={handleInstanceChange}
                  style={{ minWidth: '250px' }}
                  leftSection={<IconTopologyStar size={16} />}
                />
              )}
              
              <Select
                placeholder={`Select a ${viewType.slice(0, -1)} to view`}
                data={flowOptions}
                value={selectedFlowIndex.toString()}
                onChange={handleFlowChange}
                style={{ minWidth: '350px' }}
                disabled={currentFlows.length === 0}
              />
              <Select
                placeholder="Layout"
                data={[
                  { value: 'TB', label: 'Top to Bottom' },
                  { value: 'LR', label: 'Left to Right' }
                ]}
                value={layoutDirection}
                onChange={setLayoutDirection}
                style={{ width: '150px' }}
              />
            </Group>
          
          <Group>
            <Tooltip label="Export flow data">
              <ActionIcon onClick={handleExportData} variant="light">
                <IconDownload size={16} />
              </ActionIcon>
            </Tooltip>
            
            <Tooltip label="Routing settings">
              <ActionIcon onClick={() => setShowRoutingStats(!showRoutingStats)} variant="light" color="blue">
                <IconRoute size={16} />
              </ActionIcon>
            </Tooltip>
            
            {/* Image Export Menu */}
            {selectedFlow && (
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <Tooltip label="Export diagram as image">
                    <ActionIcon 
                      variant="light" 
                      color="blue"
                      loading={isExporting}
                      disabled={isExporting}
                    >
                      <IconPhoto size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Label>Export Current Diagram</Menu.Label>
                  <Menu.Item 
                    leftSection={<IconFileTypePng size={14} />}
                    onClick={() => exportDiagramAsImage('png')}
                    disabled={isExporting || isBatchExporting}
                  >
                    PNG (High Quality)
                  </Menu.Item>
                  <Menu.Item 
                    leftSection={<IconFileTypeJpg size={14} />}
                    onClick={() => exportDiagramAsImage('jpeg')}
                    disabled={isExporting || isBatchExporting}
                  >
                    JPEG (Compressed)
                  </Menu.Item>
                  <Menu.Item 
                    leftSection={<IconFileTypeSvg size={14} />}
                    onClick={() => exportDiagramAsImage('svg')}
                    disabled={isExporting || isBatchExporting}
                  >
                    SVG (Vector)
                  </Menu.Item>
                  <Menu.Item 
                    leftSection={<IconCloudUpload size={14} />}
                    onClick={exportToLucid}
                    disabled={isExporting || isBatchExporting || isLucidExporting}
                    rightSection={!isLucidAuthenticated ? <Badge size="xs" color="orange">Auth Required</Badge> : null}
                  >
                    Export to Lucid Charts
                  </Menu.Item>
                  
                  <Menu.Divider />
                  
                  <Menu.Label>Export All Flows ({currentFlows.length})</Menu.Label>
                  <Menu.Item 
                    leftSection={<IconFileTypePng size={14} />}
                    onClick={() => exportAllFlowsAsImages('png')}
                    disabled={isExporting || isBatchExporting || currentFlows.length === 0}
                  >
                    All as PNG (ZIP)
                  </Menu.Item>
                  <Menu.Item 
                    leftSection={<IconFileTypeJpg size={14} />}
                    onClick={() => exportAllFlowsAsImages('jpeg')}
                    disabled={isExporting || isBatchExporting || currentFlows.length === 0}
                  >
                    All as JPEG (ZIP)
                  </Menu.Item>
                  <Menu.Item 
                    leftSection={<IconFileTypeSvg size={14} />}
                    onClick={() => exportAllFlowsAsImages('svg')}
                    disabled={isExporting || isBatchExporting || currentFlows.length === 0}
                  >
                    All as SVG (ZIP)
                  </Menu.Item>
                  <Menu.Item 
                    leftSection={<IconCloud size={14} />}
                    onClick={exportAllToLucid}
                    disabled={isExporting || isBatchExporting || isLucidExporting || currentFlows.length === 0}
                    rightSection={!isLucidAuthenticated ? <Badge size="xs" color="orange">Auth Required</Badge> : null}
                  >
                    All to Lucid Charts
                  </Menu.Item>
                  
                  {!isLucidAuthenticated && (
                    <>
                      <Menu.Divider />
                      <Menu.Item 
                        leftSection={<IconKey size={14} />}
                        onClick={handleLucidAuth}
                        color="blue"
                      >
                        Connect to Lucid Charts
                      </Menu.Item>
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
            
            {/* Collapse/Expand Right Pane */}
            <Tooltip label={rightPaneCollapsed ? "Show details panel" : "Hide details panel"}>
              <ActionIcon 
                onClick={() => setRightPaneCollapsed(!rightPaneCollapsed)} 
                variant="light" 
                color="gray"
              >
                {rightPaneCollapsed ? <IconChevronLeft size={16} /> : <IconChevronRight size={16} />}
              </ActionIcon>
            </Tooltip>
            
            <Tooltip label="Close viewer">
              <ActionIcon onClick={onClose} variant="light" color="red">
                <IconX size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {/* Routing Configuration Panel */}
        <Collapse in={showRoutingStats}>
          <Card withBorder p="md">
            <Group justify="space-between" mb="sm">
              <Text fw={500}>
                <IconRoute size={16} style={{ marginRight: 8 }} />
                Routing Configuration
              </Text>
              <Text size="xs" color="dimmed">
                Advanced routing is always enabled for optimal diagram clarity
              </Text>
            </Group>
            
            <RoutingConfiguration
              currentConfig={currentRoutingConfig}
              onConfigChange={handleRoutingConfigChange}
            />
          </Card>
        </Collapse>

        

        {currentFlows.length === 0 ? (
          <Card withBorder style={{ textAlign: 'center', padding: '60px' }}>
            <IconAlertTriangle size={48} color="#ffa726" style={{ marginBottom: '16px' }} />
            <Text size="lg" fw={500} mb="sm">
              {flows.length === 0 ? `No Contact ${viewType.charAt(0).toUpperCase() + viewType.slice(1)} Available` : `No ${viewType.charAt(0).toUpperCase() + viewType.slice(1)} for Selected Instance`}
            </Text>
            <Text size="sm" color="dimmed">
              {flows.length === 0 
                ? `No processable contact ${viewType} were found in the exported data.`
                : `No contact ${viewType} found for instance: ${currentInstanceAlias}`
              }
            </Text>
            {selectedInstanceIds.length > 1 && flows.length > 0 && (
              <Text size="sm" color="dimmed" mt="sm">
                Try selecting a different instance from the dropdown above.
              </Text>
            )}
          </Card>
        ) : (
          <div style={{ display: 'flex', gap: '16px', height: '100%', minHeight: '600px' }}>
            {/* Export Progress Overlay */}
            {(isExporting || isBatchExporting || isLucidExporting) && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px'
            }}>
              <Card withBorder p="lg" style={{ textAlign: 'center' }}>
                <IconPhoto size={32} color="#228be6" style={{ marginBottom: '8px' }} />
                <Text size="sm" fw={500}>
                  {isLucidExporting ? 'Exporting to Lucid Charts...' : isBatchExporting ? 'Exporting All Flows...' : 'Exporting Diagram...'}
                </Text>
                <Text size="xs" color="dimmed" mt="xs">
                  {isLucidExporting
                    ? `${lucidExportProgress.status === 'processing' ? 'Processing' : 'Uploading'} ${lucidExportProgress.current}/${lucidExportProgress.total}: ${lucidExportProgress.flowName}`
                    : isBatchExporting 
                      ? `Batch ${batchExportProgress.batch}/${batchExportProgress.totalBatches} - Processing ${batchExportProgress.current}/${batchExportProgress.total}: ${batchExportProgress.flowName}`
                      : 'Capturing full diagram content'
                  }
                </Text>
                {isBatchExporting && (
                  <div style={{ 
                    width: '200px', 
                    height: '4px', 
                    backgroundColor: '#e0e0e0', 
                    borderRadius: '2px',
                    marginTop: '8px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${(batchExportProgress.current / batchExportProgress.total) * 100}%`,
                      height: '100%',
                      backgroundColor: '#228be6',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                )}
              </Card>
            </div>
          )}
            
            {/* Main Diagram Area */}
            <div style={{ flex: rightPaneCollapsed ? 1 : '1 1 70%', position: 'relative' }}>
              {selectedFlow ? (
                <ContactFlowDiagram
                    key={`flow-${selectedFlowIndex}-${currentInstanceId}-${currentRoutingConfig.preset || 'default'}`}
                    ref={diagramRef}
                    contactFlow={selectedFlow.originalFlow}
                    nodes={selectedFlow.nodes}
                    edges={selectedFlow.edges}
                    summary={selectedFlow.metadata}
                    onNodeClick={handleNodeClick}
                    onEdgeClick={handleEdgeClick}
                    layoutDirection={layoutDirection}
                    showMiniMap={true}
                    showControls={true}
                    fitViewOnLoad={!isBatchExporting}
                    exportedData={exportedData}
                    instanceId={currentInstanceId}
                    routingConfig={currentRoutingConfig}
                    showAISummary={showSummaryInDiagram}
                    onRenderComplete={handleDiagramRenderComplete}
                />
                ) : (
                <Card withBorder style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Text>Select a flow to view its diagram</Text>
                </Card>
              )}
            </div>

            {/* Enhanced Right Panel - Flow Details (Collapsible) */}
            {!rightPaneCollapsed && (
              <div style={{ width: '320px' }}>
                <Tabs defaultValue="details" orientation="horizontal">
                  <Tabs.List>
                    <Tabs.Tab value="details" leftSection={<IconFileAnalytics size={14} />}>
                      Details
                    </Tabs.Tab>
                    <Tabs.Tab value="analysis" leftSection={<IconChartBar size={14} />}>
                      Analysis
                    </Tabs.Tab>
                    <Tabs.Tab value="routing" leftSection={<IconRoute size={14} />}>
                      Routing
                    </Tabs.Tab>
                    {currentFlows.length > 15 && (
                      <Tabs.Tab value="export" leftSection={<IconSettings size={14} />}>
                        Export
                      </Tabs.Tab>
                    )}
                  </Tabs.List>

                  <Tabs.Panel value="details" pt="md">
                    <ScrollArea style={{ height: '500px' }}>
                      <Stack gap="md">
                        {/* Flow Information */}
                        {selectedFlow && (
                          <Card withBorder p="sm">
                            <Text size="sm" fw={500} mb="xs">Flow Information</Text>
                            <Stack gap="xs">
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Name:</Text>
                                <Text size="xs">{selectedFlow.flowName}</Text>
                              </Group>
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Type:</Text>
                                <Text size="xs">{selectedFlow.flowType}</Text>
                              </Group>
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Nodes:</Text>
                                <Badge size="sm">{selectedFlow.nodes?.length || 0}</Badge>
                              </Group>
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Connections:</Text>
                                <Badge size="sm">{selectedFlow.edges?.length || 0}</Badge>
                              </Group>
                              
                              {/* Enhanced Features */}
                              {selectedFlow.features && (
                                <div>
                                  <Text size="xs" color="dimmed" mb="xs">Features:</Text>
                                  <Group gap="xs">
                                    {selectedFlow.features.entryNode && (
                                      <Badge size="xs" color="blue">Entry Node</Badge>
                                    )}
                                    {selectedFlow.features.detailedNodes && (
                                      <Badge size="xs" color="green">Detailed Nodes</Badge>
                                    )}
                                    {selectedFlow.features.dataLookup && (
                                      <Badge size="xs" color="purple">Data Lookup</Badge>
                                    )}
                                    {selectedFlow.features.conditions && (
                                      <Badge size="xs" color="orange">Conditions</Badge>
                                    )}
                                    <Badge size="xs" color="blue">Advanced Routing</Badge>
                                  </Group>
                                </div>
                              )}
                              
                              {selectedFlow.metadata?.description && (
                                <div>
                                  <Text size="xs" color="dimmed" mb="xs">Description:</Text>
                                  <Text size="xs">{selectedFlow.metadata.description}</Text>
                                </div>
                              )}
                            </Stack>
                          </Card>
                        )}

                        {/* AI Flow Summary Card */}
                        {selectedFlow && (
                          <Card withBorder p="sm">
                            <Group mb="xs" gap="xs" justify="space-between">
                              <Group gap="xs">
                                <IconSparkles size={14} />
                                <Text size="sm" fw={500}>AI Flow Summary</Text>
                              </Group>
                              {selectedFlow?.originalFlow?.summary && (
                                <Tooltip label="Expand full summary">
                                  <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    color="blue"
                                    onClick={() => setSummaryModalOpen(true)}
                                  >
                                    <IconMaximize size={14} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Group>

                            {selectedFlow?.originalFlow?.summary ? (
                              <>
                                <ScrollArea style={{ maxHeight: '150px' }}>
                                  <Text
                                    size="xs"
                                    style={{
                                      whiteSpace: 'pre-wrap',
                                      lineHeight: 1.6,
                                      fontFamily: 'monospace'
                                    }}
                                  >
                                    {(() => {
                                      const summaryText = Array.isArray(selectedFlow.originalFlow.summary)
                                        ? selectedFlow.originalFlow.summary.join('\n\n')
                                        : selectedFlow.originalFlow.summary;
                                      const lines = summaryText.split('\n');
                                      const preview = lines.slice(0, 4).join('\n');
                                      return preview;
                                    })()}
                                  </Text>
                                </ScrollArea>
                                {(() => {
                                  const summaryText = Array.isArray(selectedFlow.originalFlow.summary)
                                    ? selectedFlow.originalFlow.summary.join('\n\n')
                                    : selectedFlow.originalFlow.summary;
                                  const lines = summaryText.split('\n');
                                  return lines.length > 4 && (
                                    <Text size="xs" c="dimmed" fs="italic" mt="xs">
                                      Click expand icon to view full summary...
                                    </Text>
                                  );
                                })()}
                              </>
                            ) : (
                              <Text size="xs" c="dimmed" fs="italic">
                                No AI summary available for this flow
                              </Text>
                            )}
                          </Card>
                        )}

                        {/* Node Details */}
                        {selectedNodeData && (
                          <Card withBorder p="sm">
                            <Text size="sm" fw={500} mb="xs">Selected Node</Text>
                            <Stack gap="xs">
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Type:</Text>
                                <Badge size="xs">{selectedNodeData.data.actionType}</Badge>
                              </Group>
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Label:</Text>
                                <Text size="xs">{selectedNodeData.data.label}</Text>
                              </Group>
                              {selectedNodeData.data.description && (
                                <div>
                                  <Text size="xs" color="dimmed" mb="xs">Description:</Text>
                                  <Text size="xs">{selectedNodeData.data.description}</Text>
                                </div>
                              )}
                              {selectedNodeData.data.displayInfo && (
                                <div>
                                  <Text size="xs" color="dimmed" mb="xs">Content:</Text>
                                  <Text size="xs" style={{ fontStyle: 'italic' }}>
                                    {selectedNodeData.data.displayInfo.displayMessage || 
                                     selectedNodeData.data.displayInfo.displayPrompt || 
                                     selectedNodeData.data.displayInfo.comparisonValue ||
                                     'No content preview available'}
                                  </Text>
                                </div>
                              )}
                            </Stack>
                          </Card>
                        )}

                        {/* Edge Details */}
                        {selectedEdgeData && (
                          <Card withBorder p="sm">
                            <Text size="sm" fw={500} mb="xs">Selected Connection</Text>
                            <Stack gap="xs">
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Type:</Text>
                                <Badge size="xs" color={
                                  selectedEdgeData.data.transitionType === 'error' ? 'red' :
                                  selectedEdgeData.data.transitionType === 'condition' ? 'blue' : 'green'
                                }>
                                  {selectedEdgeData.data.transitionType}
                                </Badge>
                              </Group>
                              
                              {selectedEdgeData.data?.label && (
                                <Group justify="space-between">
                                  <Text size="xs" color="dimmed">Label:</Text>
                                  <Text size="xs">{selectedEdgeData.data.label}</Text>
                                </Group>
                              )}
                              
                              {selectedEdgeData.data?.routedPath && (
                                <Group justify="space-between">
                                  <Text size="xs" color="dimmed">Routing:</Text>
                                  <Badge size="xs" color="blue">
                                    {selectedEdgeData.data.routedPath.length} waypoints
                                  </Badge>
                                </Group>
                              )}
                              
                              {selectedEdgeData.data?.bundleSize > 1 && (
                                <Group justify="space-between">
                                  <Text size="xs" color="dimmed">Bundle:</Text>
                                  <Badge size="xs" color="purple">
                                    {selectedEdgeData.data.bundleIndex + 1} of {selectedEdgeData.data.bundleSize}
                                  </Badge>
                                </Group>
                              )}
                            </Stack>
                          </Card>
                        )}
                      </Stack>
                    </ScrollArea>
                  </Tabs.Panel>

                  <Tabs.Panel value="analysis" pt="md">
                    <ScrollArea style={{ height: '500px' }}>
                      <Stack gap="md">
                        {selectedFlow && (
                          <>
                            <Card withBorder p="sm">
                              <Text size="sm" fw={500} mb="xs">Flow Complexity</Text>
                              <Stack gap="xs">
                                <Group justify="space-between">
                                  <Text size="xs" color="dimmed">Total Actions:</Text>
                                  <Text size="xs">{selectedFlow.nodes?.length || 0}</Text>
                                </Group>
                                <Group justify="space-between">
                                  <Text size="xs" color="dimmed">Transitions:</Text>
                                  <Text size="xs">{selectedFlow.edges?.length || 0}</Text>
                                </Group>
                                <Group justify="space-between">
                                  <Text size="xs" color="dimmed">Complexity:</Text>
                                  <Badge size="xs" color={
                                    (selectedFlow.nodes?.length || 0) <= 5 ? 'green' :
                                    (selectedFlow.nodes?.length || 0) <= 15 ? 'yellow' : 'red'
                                  }>
                                    {(selectedFlow.nodes?.length || 0) <= 5 ? 'Simple' :
                                     (selectedFlow.nodes?.length || 0) <= 15 ? 'Moderate' : 'Complex'}
                                  </Badge>
                                </Group>
                              </Stack>
                            </Card>
                            
                            {selectedFlow.validation && (
                              <Card withBorder p="sm">
                                <Text size="sm" fw={500} mb="xs">Validation Results</Text>
                                <Stack gap="xs">
                                  <Group justify="space-between">
                                    <Text size="xs" color="dimmed">Valid:</Text>
                                    <Badge size="xs" color={selectedFlow.validation.isValid ? 'green' : 'red'}>
                                      {selectedFlow.validation.isValid ? 'Yes' : 'No'}
                                    </Badge>
                                  </Group>
                                  {selectedFlow.validation.issues && selectedFlow.validation.issues.length > 0 && (
                                    <div>
                                      <Text size="xs" color="dimmed" mb="xs">Issues:</Text>
                                      {selectedFlow.validation.issues.map((issue, index) => (
                                        <Text key={index} size="xs" color="red" style={{ fontSize: '10px' }}>
                                          • {issue}
                                        </Text>
                                      ))}
                                    </div>
                                  )}
                                  {selectedFlow.validation.warnings && selectedFlow.validation.warnings.length > 0 && (
                                    <div>
                                      <Text size="xs" color="dimmed" mb="xs">Warnings:</Text>
                                      {selectedFlow.validation.warnings.map((warning, index) => (
                                        <Text key={index} size="xs" color="orange" style={{ fontSize: '10px' }}>
                                          • {warning}
                                        </Text>
                                      ))}
                                    </div>
                                  )}
                                </Stack>
                              </Card>
                            )}
                          </>
                        )}
                      </Stack>
                    </ScrollArea>
                  </Tabs.Panel>

                  <Tabs.Panel value="routing" pt="md">
                    <ScrollArea style={{ height: '500px' }}>
                      <Stack gap="md">
                        {selectedFlow && (
                          <Card withBorder p="sm">
                            <Text size="sm" fw={500} mb="xs">Routing Statistics</Text>
                            <Stack gap="xs">
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Routing Status:</Text>
                                <Badge size="xs" color="green">Active</Badge>
                              </Group>
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Edge Count:</Text>
                                <Text size="xs">{selectedFlow.edges?.length || 0}</Text>
                              </Group>
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Routed Edges:</Text>
                                <Text size="xs">
                                  {selectedFlow.edges?.filter(e => e.data?.routedPath).length || 0}
                                </Text>
                              </Group>
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Bundled Edges:</Text>
                                <Text size="xs">
                                  {selectedFlow.edges?.filter(e => e.data?.bundleSize > 1).length || 0}
                                </Text>
                              </Group>
                            </Stack>
                          </Card>
                        )}
                        
                        <Card withBorder p="sm">
                          <Text size="sm" fw={500} mb="xs">Current Configuration</Text>
                          <Stack gap="xs">
                            <Text size="xs" color="dimmed">
                              Preset: {currentRoutingConfig.preset || 'Custom'}
                            </Text>
                            <Text size="xs" color="dimmed">
                              Grid Size: {currentRoutingConfig.gridSize || 15}px
                            </Text>
                            <Text size="xs" color="dimmed">
                              Node Padding: {currentRoutingConfig.nodePadding || 25}px
                            </Text>
                            <Text size="xs" color="dimmed">
                              Bundling: {currentRoutingConfig.enableBundling ? 'Enabled' : 'Disabled'}
                            </Text>
                          </Stack>
                        </Card>
                      </Stack>
                    </ScrollArea>
                  </Tabs.Panel>

                  {currentFlows.length > 15 && (
                    <Tabs.Panel value="export" pt="md">
                      <ScrollArea style={{ height: '500px' }}>
                        <Stack gap="md">
                          <Card withBorder p="sm" style={{ backgroundColor: '#f8f9fa' }}>
                            <Text size="sm" fw={500} mb="xs">
                              <IconSettings size={16} style={{ marginRight: 8 }} />
                              Bulk Export Options
                            </Text>
                            <Stack gap="xs">
                              <Switch
                                label="Skip Default Flows"
                                description="Exclude flows where name begins with 'Default '"
                                size="xs"
                                checked={bulkExportFilters.skipDefaultFlows}
                                onChange={(e) => setBulkExportFilters(prev => ({ ...prev, skipDefaultFlows: e.currentTarget.checked }))}
                              />
                              <Switch
                                label="Skip Sample Flows"
                                description="Exclude flows where name begins with 'Sample '"
                                size="xs"
                                checked={bulkExportFilters.skipSampleFlows}
                                onChange={(e) => setBulkExportFilters(prev => ({ ...prev, skipSampleFlows: e.currentTarget.checked }))}
                              />
                              <Switch
                                label="Download local copy of Lucid Import"
                                description="Download .lucid file(s) locally for single or bulk exports"
                                size="xs"
                                checked={downloadLocalCopy}
                                onChange={(e) => setDownloadLocalCopy(e.currentTarget.checked)}
                              />
                            </Stack>
                            <Text size="xs" color="dimmed" mt="xs">
                              These filters only apply to bulk exports (all flows), not single exports.
                            </Text>
                          </Card>

                          <Card withBorder p="sm" style={{ backgroundColor: '#f8f9fa' }}>
                            <Text size="sm" fw={500} mb="xs">
                              <IconSettings size={16} style={{ marginRight: 8 }} />
                              Bulk Export Settings ({currentFlows.length} flows)
                            </Text>
                            <Stack gap="md">
                              <div>
                                <Text size="xs" color="dimmed" mb="xs">Batch Size:</Text>
                                <Select
                                  size="xs"
                                  value={batchExportConfig.batchSize.toString()}
                                  onChange={(value) => setBatchExportConfig(prev => ({ ...prev, batchSize: parseInt(value) }))}
                                  data={[
                                    { value: '5', label: '5 flows/batch (highest quality)' },
                                    { value: '8', label: '8 flows/batch (balanced)' },
                                    { value: '12', label: '12 flows/batch (recommended)' },
                                    { value: '15', label: '15 flows/batch' },
                                    { value: '20', label: '20 flows/batch (faster)' }
                                  ]}
                                  style={{ width: '100%' }}
                                />
                              </div>
                              <div>
                                <Text size="xs" color="dimmed" mb="xs">Options:</Text>
                                <Switch
                                  label="Reduce quality for faster export (lower DPI)"
                                  size="xs"
                                  checked={batchExportConfig.reduceQuality}
                                  onChange={(e) => setBatchExportConfig(prev => ({ ...prev, reduceQuality: e.currentTarget.checked }))}
                                />
                              </div>
                            </Stack>
                            <Text size="xs" color="dimmed" mt="xs">
                              Large exports will be split into {Math.ceil(currentFlows.length / batchExportConfig.batchSize)} ZIP files to prevent memory issues.
                            </Text>
                          </Card>

                          <Card withBorder p="sm">
                            <Text size="sm" fw={500} mb="xs">Export Performance</Text>
                            <Stack gap="xs">
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Total Flows:</Text>
                                <Text size="xs">{currentFlows.length}</Text>
                              </Group>
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Estimated Batches:</Text>
                                <Text size="xs">{Math.ceil(currentFlows.length / batchExportConfig.batchSize)}</Text>
                              </Group>
                              <Group justify="space-between">
                                <Text size="xs" color="dimmed">Quality Mode:</Text>
                                <Badge size="xs" color={batchExportConfig.reduceQuality ? 'orange' : 'green'}>
                                  {batchExportConfig.reduceQuality ? 'Fast' : 'High Quality'}
                                </Badge>
                              </Group>
                            </Stack>
                          </Card>

                          <Text size="xs" color="dimmed" style={{ fontStyle: 'italic' }}>
                            Use the photo export button in the toolbar to start bulk exports with these settings.
                          </Text>
                        </Stack>
                      </ScrollArea>
                    </Tabs.Panel>
                  )}
                </Tabs>
              </div>
            )}
          </div>
        )}
      </Stack>
      
      {/* Lucid Charts Authentication Modal */}
      <LucidAuthModal
        opened={lucidAuthModalOpen}
        onClose={() => setLucidAuthModalOpen(false)}
        onAuthSuccess={handleLucidAuthSuccess}
        onAuthError={handleLucidAuthError}
      />

      {/* AI Flow Summary Modal */}
      <Modal
        opened={summaryModalOpen}
        onClose={() => setSummaryModalOpen(false)}
        title={
          <Group gap="xs">
            <IconSparkles size={16} />
            <Text fw={500}>AI Flow Summary - {selectedFlow?.flowName}</Text>
          </Group>
        }
        size="xl"
        centered
        scrollAreaComponent={ScrollArea}
      >
        {selectedFlow?.originalFlow?.summary ? (
          <Box style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <ReactMarkdown
              components={{
                // Customize markdown styling
                p: ({ node, ...props }) => (
                  <Text
                    size="sm"
                    style={{
                      lineHeight: 1.8,
                      marginBottom: '1em'
                    }}
                    {...props}
                  />
                ),
                ol: ({ node, ...props }) => (
                  <Text
                    component="ol"
                    size="sm"
                    style={{
                      lineHeight: 1.8,
                      paddingLeft: '1.5em',
                      marginBottom: '1em'
                    }}
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => (
                  <Text
                    component="ul"
                    size="sm"
                    style={{
                      lineHeight: 1.8,
                      paddingLeft: '1.5em',
                      marginBottom: '1em'
                    }}
                    {...props}
                  />
                ),
                li: ({ node, ...props }) => (
                  <Text
                    component="li"
                    size="sm"
                    style={{
                      lineHeight: 1.8,
                      marginBottom: '0.5em'
                    }}
                    {...props}
                  />
                ),
              }}
            >
              {Array.isArray(selectedFlow.originalFlow.summary)
                ? selectedFlow.originalFlow.summary.join('\n\n')
                : selectedFlow.originalFlow.summary}
            </ReactMarkdown>
          </Box>
        ) : (
          <Text size="sm" c="dimmed" fs="italic">
            No AI summary available for this flow
          </Text>
        )}
      </Modal>
    </Modal>
  );
};

export default ContactFlowViewer;